import axios, { AxiosError } from 'axios'

import { APP_AUTH_ESCAPE_ERROR_CODE, STORAGE_KEYS } from '~constants'
import Mixpanel from '~utils/mixpanel'
import getRootPath from './app/get-root-path'
import Sentry from './sentry'

export const api = axios.create({
  baseURL: import.meta.env.VITE_HOST,
  timeout: 20000,
  headers: {
    'Time-Zone': new Intl.DateTimeFormat().resolvedOptions().timeZone,
  },
})

api.defaults.headers.common['access-env'] = 'web'
axios.defaults.headers.common['access-env'] = 'web'

const showMessage = async (error: Error) => {
  if (!(error instanceof AxiosError)) {
    return
  }

  if (error.response) {
    console.warn(`[Axios] error with status ${error.response.status}`, error.response)
  }
}

const reset = () => {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
  localStorage.removeItem(STORAGE_KEYS.GUEST_TOKEN)
  document.location.href = getRootPath() ?? '/'
  Mixpanel.reset()
}

api.interceptors.request.use(
  req => {
    req.baseURL = import.meta.env.VITE_HOST

    if (req.withCredentials && typeof window !== 'undefined') {
      const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
      const anonymousToken = localStorage.getItem(STORAGE_KEYS.GUEST_TOKEN)

      if (token && req.headers) {
        req.headers.Authorization = `JWT ${token}`
      } else if (anonymousToken && req.headers) {
        req.headers.Authorization = `JWT ${anonymousToken}`
      }
    }
    return req
  },
  error => {
    Sentry.captureException(error, {
      extra: {
        error,
        scope: 'API Request Error',
      },
    })

    return Promise.reject(error)
  }
)

api.interceptors.response.use(
  res => res,
  async error => {
    if (error.response && error.response.status === 307) {
      const redirectUrl = error.response.headers.location
      window.location.href = redirectUrl
      return Promise.resolve()
    }

    showMessage(error)
    const serverSideErrorMessage = error.response?.data?.error?.message
    if (serverSideErrorMessage) {
      error.message = serverSideErrorMessage
    }

    Sentry.captureException({
      exception: error,
      extras: {
        error,
        response: error.response,
        request: error.request,
        config: error.config,
        reason: error.response?.data,
      },
      scope: 'API Response Error',
    })

    if (!error.response) {
      return Promise.reject(error)
    }

    const { status } = error.response
    const serverErrorCode = error.response.data?.error?.code

    if (status === 401 && serverErrorCode === APP_AUTH_ESCAPE_ERROR_CODE) {
      reset()
    }

    return Promise.reject(error)
  }
)

export default api
